<template>
  <b-row
    class="question-form"
    v-bind:class="`question-type-${this.jobQuestionType}`"
  >
    <b-col class="text-left question-form__question" md="6">
      <div class="preamble" v-if="hasPreamble" v-html="preamble"></div>

      <div class="prompt" v-html="prompt"></div>

      <div class="explanation" v-if="hasExplanation" v-html="explanation"></div>
    </b-col>
    <b-col md="6" class="question-form__answer">
      <!-- --------------------- -->

      <div v-if="jobQuestionType === 'SELECT_ONE'">
        <template v-for="v in jobQuestion.jobQuestionVariables">
          <b-form-radio-group
            class="text-left"
            :key="v.id"
            @change="selectOneRadioChanged(v.id)"
            v-model="responses[v.id || v.uid].value"
            v-if="!v.delete_me"
          >
            <b-form-radio :value="'1'" class="col-12 my-2">
              {{ v.input_label }}
            </b-form-radio>
          </b-form-radio-group>
        </template>
      </div>

      <!-- --------------------- -->

      <div v-if="jobQuestionType === 'MULTI_VARIABLE_SELECT_ONE'">
        <multi-variable-select-one
          :variables="jobQuestion.jobQuestionVariables"
          :is-preview="preview"
        />
      </div>

      <!-- --------------------- -->

      <div v-if="jobQuestionType === 'YES_NO'">
        <b-form-radio-group
          class="text-left"
          v-model="jQYesNo"
          v-if="!jobQuestion.jobQuestionVariables[0].delete_me"
        >
          <b-form-radio :value="'1'" class="col-12 my-2">
            Yes
          </b-form-radio>
          <b-form-radio :value="'0'" class="col-12 my-2">
            No
          </b-form-radio>
        </b-form-radio-group>
      </div>

      <!-- --------------------- -->

      <div v-if="jobQuestionType === 'SELECT_ANY'">
        <template v-for="v in jobQuestion.jobQuestionVariables">
          <b-row class="text-left" :key="v.id" v-if="!v.delete_me">
            <span :id="`variable_wrapper_${v.id || v.uid}_${previewType}`">
              <b-form-checkbox
                class="my-2"
                :disabled="
                  isSelectAnyCheckboxDisabledByMaxSelections(v) ||
                  isSelectAnyCheckBoxDisabledByExclusive(v)
                "
                v-model="responses[v.id || v.uid].value"
                :value="'1'"
                :unchecked-value="null"
                @change="onSelectAnyChange(v)"
              >
                {{ v.input_label }}
              </b-form-checkbox>
            </span>
            <b-tooltip
              v-if="isSelectAnyCheckboxDisabledByMaxSelections(v)"
              :target="`variable_wrapper_${v.id || v.uid}_${previewType}`"
              >You can only pick a maximum of
              {{ maximumSelections }} selections.</b-tooltip
            >
            <b-tooltip
              v-else-if="isSelectAnyCheckBoxDisabledByExclusive(v)"
              :target="`variable_wrapper_${v.id || v.uid}_${previewType}`"
              triggers="focus click"
              noninteractive
              >{{ disabledByExclusiveTooltipText }}</b-tooltip
            >
            <b-tooltip
              v-else-if="
                exclusiveVariable &&
                (exclusiveVariable.id === v.id ||
                  exclusiveVariable.id === v.uid) &&
                anyNonExclusiveSelected
              "
              :target="`variable_wrapper_${v.id || v.uid}_${previewType}`"
              triggers="focus click"
            >
              This option cannot be selected when any other option is already
              selected.
            </b-tooltip>
          </b-row>
        </template>
      </div>

      <!-- --------------------- -->

      <div v-if="jobQuestionType === 'RATING'">
        <template v-for="(v, n) in jobQuestion.jobQuestionVariables">
          <b-row :key="n" v-if="!v.delete_me">
            <b-col>
              <label class="font-weight-bold">{{ v.input_label }}</label>
              <b-form-radio-group
                class="mb-3"
                v-model="responses[v.id || v.uid].value"
              >
                <b-form-radio v-for="i in 5" :key="i" :value="i">
                  {{ i }}
                </b-form-radio>
              </b-form-radio-group>
            </b-col>
          </b-row>
        </template>
      </div>

      <!-- --------------------- -->

      <div
        v-if="jobQuestionType === 'SLIDER'"
        class="question-form__slider-wrapper mt-3"
        :class="[
          'question-form__slider-wrapper',
          'mt-3',
          { 'required-question': jobQuestion.required },
        ]"
      >
        <b-row
          v-for="(v, n) in jobQuestion.jobQuestionVariables.filter(
            (v) => !v.delete_me
          )"
          :key="n"
        >
          <b-col
            :class="{
              'border-bottom':
                n !== jobQuestion.jobQuestionVariables.length - 1,
              'mb-3': n !== jobQuestion.jobQuestionVariables.length - 1,
              'pb-2': n !== jobQuestion.jobQuestionVariables.length - 1,
            }"
          >
            <div class="font-weight-bold text-center phairify-heading-h2">
              {{ v.input_label }}
            </div>
            <!-- if we have a single input, scooch it down a bit -->
            <QuestionFormInputSlider
              :range="false"
              :on-set="sliderUpdated"
              :preview="preview"
              :uid="'' + (v.id || v.uid)"
              :value="previewSliderResponse.value"
              :variable="v"
            />
          </b-col>
        </b-row>
        <b-row v-if="jobQuestion.calculate_totals" class="totals">
          <b-col sm="6" offset="6">
            <b-alert
              show
              class="total-alert"
              :variant="variableValuesTotal.variant"
            >
              <label class="total-label">Total:</label>
              {{ variableValuesTotal.val }}
            </b-alert>
          </b-col>
        </b-row>
      </div>

      <!-- ------- Decline to answer --------- -->

      <b-row
        v-if="!jobQuestion.required && this.jobQuestionType !== 'CONFIRMATION'"
        class="text-left decline-wrapper mt-3"
      >
        <b-col cols="12">
          <b-form-checkbox
            v-model="jQDecline"
            :value="1"
            :unchecked-value="0"
            class="my-2"
          >
            Decline to answer
          </b-form-checkbox>
        </b-col>
      </b-row>
    </b-col>
  </b-row>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>
import map from 'lodash/map';
import states from '@/providers/States.js';
import regions from '@/providers/Regions.js';
import QuestionFormInputSlider from '@/components/QuestionFormInputSlider.vue';
import DerivedVariables from '@/providers/DerivedVariables';
import Utils from '@/providers/Utils';
import { QUESTION_TYPES } from '@/store';
import MultiVariableSelectOne from '@/components/question/form/inputs/MultiVariableSelectOne';

export default {
  name: 'JobQuestionPreview',
  components: {
    QuestionFormInputSlider,
    MultiVariableSelectOne,
  },
  props: {
    survey: Object,
    jobQuestion: Object,
    jobQuestionVariables: Array, // empty in preview mode
    preview: {
      type: Boolean,
      default: true,
    },
    previewType: {
      validator: (prop) => ['member', 'talent'].includes(prop),
      default: 'member',
    },
    exclusiveId: {
      type: Number,
      default: null,
    },
  },
  data() {
    const r = {};

    this.jobQuestionVariables.forEach((v) => {
      const id = v.id || v.uid;
      r[id] = {
        value: 0,
        declined: 0,
      };
    });
    return {
      previewSliderResponse: { value: 0 },
      initialSchool: '',
      responses: r,
      jQDecline: 0,
      jQYesNo: 0,
    };
  },
  computed: {
    disabledByExclusiveTooltipText: function () {
      if (!this.exclusiveVariable) return null;
      return `This option cannot be selected when ‘${this.exclusiveVariable.input_label}’ is already selected.`;
    },
    variableValuesTotal() {
      return { variant: null };
    },
    answeredVariables: function () {
      return this.jobQuestion.jobQuestionVariables.filter((v) => {
        return (
          this.responses[v.id || v.uid] &&
          parseInt(this.responses[v.id || v.uid].value) === 1
        );
      });
    },
    maximumSelections: function () {
      return this.jobQuestion.maximum_selections;
    },
    preamble: function () {
      return Utils.replaceUnderlineMarkersWithTags(
        this.jobQuestion[`${this.previewType}_preamble`]
      );
    },
    prompt: function () {
      return Utils.replaceUnderlineMarkersWithTags(
        this.jobQuestion[`${this.previewType}_prompt`]
      );
    },
    explanation: function () {
      return Utils.replaceUnderlineMarkersWithTags(
        this.jobQuestion[`${this.previewType}_explanation`]
      );
    },
    exclusiveVariable: function () {
      if (this.jobQuestionType !== QUESTION_TYPES.SELECT_ANY) return null;
      return this.jobQuestionVariables.find(
        (v) => v.id === this.exclusiveId || v.uid === this.exclusiveId
      );
    },
    anyNonExclusiveSelected: function () {
      if (this.jobQuestionType !== QUESTION_TYPES.SELECT_ANY) {
        return false;
      }
      return this.jobQuestionVariables.some((variable) => {
        return (
          this.responses[variable.id || variable.uid].value === '1' &&
          !variable[`is_exclusive_${this.previewType}`]
        );
      });
    },
    hasPreamble: function () {
      return this.jobQuestion.hasOwnProperty(`${this.previewType}_preamble`);
    },
    hasExplanation: function () {
      return this.jobQuestion.hasOwnProperty(`${this.previewType}_explanation`);
    },
    jobQuestionType: function () {
      return this.previewType === 'talent'
        ? this.jobQuestion.talent_type
        : this.jobQuestion.type;
    },
  },
  methods: {
    getStates() {
      return states.states;
    },
    getStateOptions() {
      return map(states.states, (name, key) => {
        return { value: key, text: name };
      });
    },
    getRegionOptions() {
      return map(regions.regions, (name, key) => {
        return { value: key, text: name };
      });
    },
    getSchoolOptions() {
      // TODO: add real school list
      return [
        'Brown',
        'Cornell',
        'Columbia',
        'Dartmouth',
        'Harvard',
        'Penn',
        'Princeton',
        'Yale',
      ];
    },
    getResponseValue(variable) {
      if (this.responses[variable.id]) {
        return this.responses[variable.id].value;
      } else {
        return {};
      }
    },
    formatDerivedVariableResponseValue(v) {
      if (
        this.responses[v.id || v.uid] &&
        this.responses[v.id || v.uid].value &&
        v.dv_name
      ) {
        const dv = DerivedVariables.calculationVariables[v.dv_name];
        const prefix = dv.prefix;
        const num = parseFloat(
          this.responses[v.id || v.uid].value
        ).toLocaleString('en', {
          currency: 'USD',
          maximumFractionDigits: 0,
        });
        return prefix + num;
      } else {
        return '';
      }
    },
    sliderUpdated(uid, val) {
      if (!this.preview) {
        const variable = this.jobQuestion.jobQuestionVariables.find(
          (v) => '' + v.id === uid
        );
        this.responses[variable.id || variable.uid].value = val;
      } else {
        this.previewSliderResponse.value = val;
      }
    },
    selectOneRadioChanged(checkedId) {
      this.jobQuestion.jobQuestionVariables.forEach((v) => {
        this.responses[v.id || v.uid].value = v.id === checkedId ? '1' : 0;
      });
    },
    onSelectAnyVarChange: function (value, variable) {
      this.$nextTick(() => {
        if (
          this.isSelectAnyCheckBoxDisabledByExclusive(variable) ||
          (this.exclusiveId &&
            this.exclusiveId === variable.id &&
            this.anyNonExclusiveSelected)
        ) {
          this.responses[variable.id || variable.uid].value = null;
        }
      });
    },
    isSelectAnyCheckboxDisabledByMaxSelections: function (v) {
      if (
        (this.responses[v.id || v.uid] &&
          parseInt(this.responses[v.id || v.uid].value) === 1) ||
        this.jobQuestion.maximum_selections === 0
      ) {
        return false;
      }
      return (
        this.jobQuestion.maximum_selections <= this.answeredVariables.length
      );
    },
    isSelectAnyCheckBoxDisabledByExclusive: function (v) {
      if (!this.exclusiveId) return false;
      //if current variable is not exclusive and exclusive variable is checked
      return (
        this.exclusiveId !== (v.id || v.uid) &&
        this.responses[this.exclusiveId].value === '1'
      );
    },
    onSelectAnyChange: function (v) {
      this.jQDecline = 0;
      if (this.exclusiveId && v.id === this.exclusiveId) {
        Object.keys(this.responses).forEach((id) => {
          if (parseInt(id) !== parseInt(this.exclusiveId)) {
            this.responses[id].value = null;
          }
        });
      }
    },
  },
  watch: {
    jobQuestionVariables: function () {
      this.jobQuestionVariables.forEach((v) => {
        const id = v.id || v.uid;
        if (!this.responses[id]) {
          this.$set(this.responses, id, {});
          this.$set(this.responses[id], 'value', 0);
        }
      });
    },
  },
};
</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss" scoped>
// include this in any Vue component where you want to reference vars or mixins
@import '../../../../sass/variables_mixins.scss';

.question-form {
  border-radius: 4px;
  overflow: hidden;

  > .row {
    //align-items: center;
  }
  div.border-bottom {
    border-bottom-color: $phairify_primary_border_color !important;
  }
}

.question-form__question {
  font-size: 1.25em;
  color: $phairify_dark_sky_blue;
  background-color: rgba($phairify_primary_color_teal, 0.1);
  @media (max-width: $tablet-portrait - 1px) {
    padding: 2em 1em;
  }
  @media (min-width: $tablet-portrait) {
    padding: 2em;
  }
}

.question-form__answer {
  padding-top: 1em;
  //border:solid 1px #ccc;
  background-color: #fff;
  @media (max-width: $tablet-portrait - 1px) {
    //border-top: none;
    //border-radius: 0 0 5px 5px;
  }
  @media (min-width: $tablet-portrait) {
    //border-left:none;
    //border-radius:0 5px 5px 0;
  }

  .custom-checkbox {
    margin-left: 1em;
  }
  .question-form__slider-wrapper {
    height: calc(100% - 100px);
    display: flex;
    flex-direction: column;
    justify-content: center;
    &.required-question {
      height: 100%;
      padding-bottom: 1.5rem;
    }
  }
}

.preamble {
  font-style: italic;
}

.prompt {
  font-weight: bold;
}

.explanation {
  font-family: 'Questrial', sans-serif;
  margin: 20px 40px 20px 0;
  border-radius: 5px;
  font-style: italic;
}

.decline-wrapper {
  border-top: solid 1px $phairify_primary_border_color;
  padding-top: 10px;
  margin-top: 35px;
}

.totals {
  .alert {
    margin-top: 0.5rem;

    &.alert-success {
      background: $base-color !important;
      color: #fff;
    }

    &.alert-danger {
      background-color: $light-red !important;
      color: #fff;
    }
  }
}

.total-alert {
  position: fixed;
  bottom: 30px;
  right: 30px;
}

.total-label {
  padding: 0;
  margin: 0 10px 0 0;
}

td.rating-radios-label {
  white-space: nowrap;
  text-align: left;
  padding: 8px 12px;
}

td.rating-radios {
  white-space: nowrap;
  border: solid 1px #ccc;
  text-align: center;
  padding: 8px 12px;
}

// for sliders
.noUi-connect {
  background-color: $accent-color !important;
}
</style>
